import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { DashboardLayout } from "@/pages/Dashboard";
import TableWithSearch from "@/components/TableWithSearch";
import FilterDropdown from "@/components/FilterDropDowns";
import type { ColumnHeader } from "@/components/MyTable";
import { formatDateTime12 } from "@/utils/helpers";
import { PatientInfoCard } from "../components/PatientInfoCard";
import {
  getLegacyDateRangeParams,
  getLegacyMonthFilters,
  LEGACY_DEFAULT_HIDDEN_COLUMN_IDS,
  LEGACY_MONTH_FILTER_OPTIONS,
} from "../legacyTableDefaults";

const readingHeaders: ColumnHeader[] = [
  { id: "sourcePatientId", label: "ID", isSortable: false },
  { id: "patientName", label: "Patient Name", isSortable: false },
  { id: "dob", label: "DOB", isSortable: false },
  { id: "icdCodes", label: "ICD Codes", isSortable: false },
  { id: "primaryInsurance", label: "Primary Insurance", isSortable: false },
  { id: "insuranceId", label: "Insurance ID", isSortable: false },
  { id: "address", label: "Address", isSortable: false },
  { id: "deviceAssigned", label: "Device Assigned", isSortable: false },
  { id: "readingDate", label: "Date", isSortable: false },
  { id: "bp", label: "BP (Systolic/Diastolic)", isSortable: false },
  { id: "pulse", label: "Pulse", isSortable: false },
  { id: "spo2", label: "SpO2", isSortable: false },
  { id: "weightLbs", label: "Weight (lbs)", isSortable: false },
  { id: "glucoseLevel", label: "Glucose Level", isSortable: false },
];

function getFirstValue(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
}

function formatListValue(value: unknown) {
  const formatted = Array.isArray(value) ? value.join(", ") : value || "";
  return String(formatted).replace(/~/g, ", ");
}

function formatLegacyDateTime(value: unknown) {
  if (!value) return "";

  const formatted = formatDateTime12(value as string | number | Date);
  return formatted || String(value);
}

function formatBloodPressure(reading: Record<string, unknown>) {
  const raw = getFirstValue(reading, ["bpRaw", "BP Raw", "BP raw"]);
  if (raw) return raw;

  const systolic = getFirstValue(reading, ["bpSystolic", "BP Systolic", "systolic"]);
  const diastolic = getFirstValue(reading, ["bpDiastolic", "BP Diastolic", "diastolic"]);
  return systolic && diastolic ? `${systolic}/${diastolic}` : "";
}

const EMPTY_FILTERS = {};
const NOOP = () => {};

function mapReadingRows(rows: any[]) {
  return rows.map((reading: any) => ({
    ...reading,
    sourcePatientId: getFirstValue(reading, ["sourcePatientId", "patientId", "patientID"]),
    patientName: getFirstValue(reading, ["patientName", "name"]),
    dob: getFirstValue(reading, ["dob", "dateOfBirthLocal", "dateOfBirth"]),
    icdCodes: formatListValue(getFirstValue(reading, ["icdCodes", "icdCode"])),
    primaryInsurance: getFirstValue(reading, [
      "primaryInsurance",
      "primaryInsuranceName",
    ]),
    insuranceId: getFirstValue(reading, [
      "insuranceId",
      "insuranceID",
      "primaryInsuranceNumber",
    ]),
    address:
      getFirstValue(reading, ["address"]) ||
      [reading?.street, reading?.city, reading?.state, reading?.zipCode]
        .filter(Boolean)
        .join(", "),
    deviceAssigned: getFirstValue(reading, [
      "deviceAssigned",
      "device",
      "deviceName",
      "readingType",
      "type",
    ]),
    readingDate: formatLegacyDateTime(
      getFirstValue(reading, [
        "readingDateLocal",
        "readingDate",
        "dateTime",
        "createdAt",
        "date",
      ]),
    ),
    bp: formatBloodPressure(reading),
    pulse: getFirstValue(reading, ["pulse", "Pulse", "heartRate", "bpm"]),
    spo2: getFirstValue(reading, ["spO2", "SpO2", "spo2", "oxygenSaturation"]),
    weightLbs: getFirstValue(reading, [
      "weightLbs",
      "Weight LBS",
      "weight",
      "Weight",
      "LBS",
    ]),
    glucoseLevel: getFirstValue(reading, [
      "glucoseLevel",
      "Glucose Level",
      "glucose level",
      "glucose",
    ]),
  }));
}

export default function LegacyReadings() {
  const { patientId } = useParams();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState<Record<string, string>>(() => getLegacyMonthFilters());
  const requestParams = useMemo(
    () => ({
      patientId,
      skip: 0,
      limit: 20,
      ...getLegacyDateRangeParams(filters.month),
    }),
    [filters.month, patientId],
  );
  const viewData = useMemo(() => mapReadingRows(rows), [rows]);

  return (
    <DashboardLayout activePage="/legacy">
      <div className="space-y-4">
        <PatientInfoCard />
        <TableWithSearch
          heading="Past Medical Records Readings"
          url="/legacy-readings"
          params={requestParams}
          apiDataKey="readings"
          fetchCondition={!!patientId}
          universalLoadingSkeleton
          isIncludeSearch={false}
          searchPlaceholder="Search readings"
          headerExtraControls={
            <FilterDropdown
              filters={filters}
              setFilters={setFilters}
              filtersOptions={LEGACY_MONTH_FILTER_OPTIONS}
              defaultFilters={getLegacyMonthFilters()}
              isHideClearAllFilters
            />
          }
          columnHeaders={readingHeaders}
          data={rows}
          viewData={viewData}
          setData={setRows}
          isActionColumn={false}
          filtersOptions={[]}
          handleAction={NOOP}
          filters={EMPTY_FILTERS}
          setFilters={NOOP}
          clickRow={NOOP}
          isColumnSelection
          columnPreferenceKey="legacy-readings-v5"
          defaultHiddenColumnIds={LEGACY_DEFAULT_HIDDEN_COLUMN_IDS}
          isIgnoreSearchParams
        />
      </div>
    </DashboardLayout>
  );
}
